import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';

export const generatePDF = (result) => {
  const doc = new jsPDF();
  
  // Colors
  const primaryColor = [15, 23, 42]; // #0f172a
  const accentColor = [30, 64, 175]; // #1e40af
  const textColor = [51, 65, 85]; // #334155
  const lightGray = [241, 245, 249]; // #f1f5f9
  
  // Helper functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (rate) => {
    return `${rate.toFixed(2)}%`;
  };

  // Header
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('JurisCalc NJ', 20, 22);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('New Jersey Post-Judgment Interest Calculation', 20, 32);
  
  // Date generated
  doc.setFontSize(9);
  doc.text(`Generated: ${format(new Date(), 'MMMM d, yyyy h:mm a')}`, 140, 32);
  
  // Reset text color
  doc.setTextColor(...textColor);
  
  // Summary Section - adjust height based on court costs
  let yPos = 55;
  const hasCourtCosts = result.court_costs && result.court_costs > 0;
  const summaryHeight = hasCourtCosts ? 55 : 45;
  
  doc.setFillColor(...lightGray);
  doc.roundedRect(15, yPos - 5, 180, summaryHeight, 2, 2, 'F');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('CALCULATION SUMMARY', 20, yPos + 5);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);
  
  // Left column
  doc.text(`Judgment Date: ${format(new Date(result.judgment_date), 'MMMM d, yyyy')}`, 20, yPos + 17);
  doc.text(`Calculation Date: ${format(new Date(result.calculation_date), 'MMMM d, yyyy')}`, 20, yPos + 27);
  doc.text(`Total Days: ${result.total_days.toLocaleString()}`, 20, yPos + 37);
  
  // Right column
  doc.text(`Judgment Amount: ${formatCurrency(result.judgment_amount)}`, 110, yPos + 17);
  doc.text(`Total Interest: ${formatCurrency(result.total_interest)}`, 110, yPos + 27);
  
  if (hasCourtCosts) {
    doc.text(`Court Costs: ${formatCurrency(result.court_costs)}`, 110, yPos + 37);
    // Total Amount Due (highlighted)
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accentColor);
    doc.text(`Total Amount Due: ${formatCurrency(result.total_amount_due)}`, 110, yPos + 47);
  } else {
    // Total Amount Due (highlighted)
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...accentColor);
    doc.text(`Total Amount Due: ${formatCurrency(result.total_amount_due)}`, 110, yPos + 37);
  }
  
  // Additional rate notice
  yPos += hasCourtCosts ? 65 : 55;
  if (result.is_over_threshold) {
    doc.setFillColor(219, 234, 254); // light blue
    doc.roundedRect(15, yPos - 5, 180, 12, 2, 2, 'F');
    doc.setFontSize(9);
    doc.setTextColor(...accentColor);
    doc.text('Note: Judgment exceeds $20,000 threshold. Additional 2% rate applied per N.J.S.A. 2A:17-56.51', 20, yPos + 3);
  }
  
  // Year-by-Year Breakdown Table
  yPos += 20;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('YEAR-BY-YEAR BREAKDOWN', 20, yPos);
  
  yPos += 8;
  
  // Table data
  const tableData = result.yearly_breakdown.map(row => [
    row.year.toString(),
    `${format(new Date(row.start_date), 'MM/dd')} - ${format(new Date(row.end_date), 'MM/dd')}`,
    row.days.toString(),
    formatPercentage(row.base_rate),
    row.additional_rate > 0 ? formatPercentage(row.additional_rate) : '—',
    formatPercentage(row.effective_rate),
    formatCurrency(row.interest),
    formatCurrency(row.running_total)
  ]);
  
  doc.autoTable({
    startY: yPos,
    head: [['Year', 'Period', 'Days', 'Base Rate', '+2% Add.', 'Eff. Rate', 'Interest', 'Running Total']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 8,
      textColor: textColor
    },
    columnStyles: {
      0: { halign: 'center', fontStyle: 'bold' },
      1: { halign: 'center' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right', fontStyle: 'bold', textColor: accentColor },
      6: { halign: 'right', textColor: [21, 128, 61] }, // green
      7: { halign: 'right', fontStyle: 'bold' }
    },
    margin: { left: 15, right: 15 },
    tableWidth: 'auto'
  });
  
  // Final totals box
  const finalY = doc.lastAutoTable.finalY + 10;
  
  doc.setFillColor(...primaryColor);
  doc.roundedRect(100, finalY, 95, 30, 2, 2, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('TOTAL INTEREST', 110, finalY + 10);
  doc.text('TOTAL AMOUNT DUE', 110, finalY + 22);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(formatCurrency(result.total_interest), 175, finalY + 10, { align: 'right' });
  doc.text(formatCurrency(result.total_amount_due), 175, finalY + 22, { align: 'right' });
  
  // Legal Disclaimer Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184); // slate-400
  
  const disclaimer = 'Disclaimer: This calculation is provided for informational purposes only and should not be considered legal advice. Interest rates are based on the New Jersey Court Rules. For judgments over $20,000, an additional 2% is added to the base rate per N.J.S.A. 2A:17-56.51. Please verify all calculations with official court records and consult with a licensed attorney for legal matters.';
  
  const splitDisclaimer = doc.splitTextToSize(disclaimer, 180);
  doc.text(splitDisclaimer, 15, pageHeight - 20);
  
  // Page number
  doc.setTextColor(...textColor);
  doc.text('Page 1 of 1', 185, pageHeight - 10, { align: 'right' });
  
  // Save the PDF
  const filename = `NJ_Interest_Calculation_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.pdf`;
  doc.save(filename);
};
